// src/hooks/useReferentQuota.ts - Hooks pour calculer les quotas des référents

import { useQuery } from '@tanstack/react-query';
import { useReferencesByReferentAndMonth } from './useReferences';
import { ReferentQuota, ReferentStats, Reference } from '../types/referents';
import { useReferents } from './useReferents';
import { useReferences } from './useReferences';
import { usePermissions } from './usePermissions';
import dayjs from 'dayjs';

/**
 * Hook pour calculer le quota mensuel d'un référent
 */
export const useReferentQuota = (
  referentId: string | undefined,
  month: number,
  year: number
) => {
  const { data: references = [] } = useReferencesByReferentAndMonth(referentId, month, year);

  return useQuery({
    queryKey: ['referentQuota', referentId, month, year],
    queryFn: async (): Promise<ReferentQuota | null> => {
      if (!referentId || references.length === 0) {
        return null;
      }

      const totalSales = references.length;
      const totalAmount = references.reduce((sum, ref) => sum + ref.saleAmount, 0);
      const totalQuota = references.reduce((sum, ref) => sum + ref.quotaAmount, 0);
      const averageQuotaPercentage = references.length > 0 
        ? references[0].quotaPercentage 
        : 0; // Dénormalisé, devrait être identique pour toutes les références

      return {
        referentId,
        referentName: references[0]?.referentName || '',
        month,
        year,
        totalSales,
        totalAmount,
        totalQuota,
        averageQuotaPercentage,
      };
    },
    enabled: !!referentId && month >= 1 && month <= 12 && year > 0,
  });
};

/**
 * Hook pour récupérer l'historique des quotas d'un référent pour une année
 */
export const useReferentQuotaHistory = (
  referentId: string | undefined,
  year: number
) => {
  // Récupérer toutes les références de l'année
  const startDate = dayjs(`${year}-01-01`).startOf('year').toDate();
  const endDate = dayjs(`${year}-12-31`).endOf('year').toDate();

  const { data: allReferences = [] } = useReferences({
    referentId,
    startDate,
    endDate,
  });

  return useQuery({
    queryKey: ['referentQuotaHistory', referentId, year],
    queryFn: async (): Promise<ReferentQuota[]> => {
      if (!referentId || allReferences.length === 0) {
        return [];
      }

      // Grouper par mois
      const monthlyData = new Map<string, Reference[]>();

      allReferences.forEach(ref => {
        const month = dayjs(ref.saleDate).month() + 1; // 1-12
        const key = `${year}-${month}`;
        if (!monthlyData.has(key)) {
          monthlyData.set(key, []);
        }
        monthlyData.get(key)!.push(ref);
      });

      // Calculer le quota pour chaque mois
      const quotas: ReferentQuota[] = [];

      for (let month = 1; month <= 12; month++) {
        const key = `${year}-${month}`;
        const monthReferences = monthlyData.get(key) || [];

        if (monthReferences.length > 0) {
          const totalSales = monthReferences.length;
          const totalAmount = monthReferences.reduce((sum, ref) => sum + ref.saleAmount, 0);
          const totalQuota = monthReferences.reduce((sum, ref) => sum + ref.quotaAmount, 0);
          const averageQuotaPercentage = monthReferences[0]?.quotaPercentage || 0;

          quotas.push({
            referentId,
            referentName: monthReferences[0]?.referentName || '',
            month,
            year,
            totalSales,
            totalAmount,
            totalQuota,
            averageQuotaPercentage,
          });
        } else {
          // Ajouter un mois vide pour avoir un historique complet
          quotas.push({
            referentId,
            referentName: allReferences[0]?.referentName || '',
            month,
            year,
            totalSales: 0,
            totalAmount: 0,
            totalQuota: 0,
            averageQuotaPercentage: 0,
          });
        }
      }

      return quotas;
    },
    enabled: !!referentId && year > 0,
  });
};

/**
 * Hook pour récupérer les statistiques d'un référent
 */
export const useReferentStats = (referentId: string | undefined) => {
  const { data: allReferences = [] } = useReferences({
    referentId,
  });

  return useQuery({
    queryKey: ['referentStats', referentId],
    queryFn: async (): Promise<ReferentStats | null> => {
      if (!referentId || allReferences.length === 0) {
        return {
          referentId: referentId || '',
          referentName: '',
          totalReferences: 0,
          totalQuota: 0,
          currentMonthQuota: 0,
        };
      }

      const now = dayjs();
      const currentMonth = now.month() + 1;
      const currentYear = now.year();

      const currentMonthReferences = allReferences.filter(ref => {
        const refDate = dayjs(ref.saleDate);
        return refDate.month() + 1 === currentMonth && refDate.year() === currentYear;
      });

      const totalReferences = allReferences.length;
      const totalQuota = allReferences.reduce((sum, ref) => sum + ref.quotaAmount, 0);
      const currentMonthQuota = currentMonthReferences.reduce((sum, ref) => sum + ref.quotaAmount, 0);

      const sortedReferences = [...allReferences].sort((a, b) => 
        dayjs(b.saleDate).valueOf() - dayjs(a.saleDate).valueOf()
      );
      const lastReferenceDate = sortedReferences[0]?.saleDate;

      return {
        referentId,
        referentName: allReferences[0]?.referentName || '',
        totalReferences,
        totalQuota,
        currentMonthQuota,
        lastReferenceDate,
      };
    },
    enabled: !!referentId,
  });
};

/**
 * Hook pour récupérer les statistiques de tous les référents (pour la page liste)
 */
export const useAllReferentsStats = () => {
  const permissions = usePermissions();
  const { data: referents = [] } = useReferents({ isActive: true });
  const { data: allReferences = [] } = useReferences();

  return useQuery({
    queryKey: ['allReferentsStats'],
    queryFn: async (): Promise<ReferentStats[]> => {
      if (referents.length === 0) {
        return [];
      }
      const now = dayjs();
      const currentMonth = now.month() + 1;
      const currentYear = now.year();

      return referents.map(referent => {
        const referentReferences = allReferences.filter(ref => ref.referentId === referent.id);
        const currentMonthReferences = referentReferences.filter(ref => {
          const refDate = dayjs(ref.saleDate);
          return refDate.month() + 1 === currentMonth && refDate.year() === currentYear;
        });

        const totalReferences = referentReferences.length;
        const totalQuota = referentReferences.reduce((sum, ref) => sum + ref.quotaAmount, 0);
        const currentMonthQuota = currentMonthReferences.reduce((sum, ref) => sum + ref.quotaAmount, 0);

        const sortedReferences = [...referentReferences].sort((a, b) => 
          dayjs(b.saleDate).valueOf() - dayjs(a.saleDate).valueOf()
        );
        const lastReferenceDate = sortedReferences[0]?.saleDate;

        return {
          referentId: referent.id,
          referentName: referent.fullName,
          totalReferences,
          totalQuota,
          currentMonthQuota,
          lastReferenceDate,
        };
      });
    },
    enabled: referents.length > 0 && (permissions?.canViewReferents ?? false),
  });
};

